import { CoinbaseWalletName } from '@solana/wallet-adapter-wallets';
import { BASE_NAVIGATION_OPTIONS } from 'src/components/Navigation/config/consts';
import { RouteNavigationResult } from 'src/components/Navigation/types';

// ⭐️⭐️⭐️ If your event should not obey sampling remember to add it to SAMPLING_INDEPENDENT_ENABLED_EVENTS below
export type PosthogEvent =
	| 'submitted_borrow'
	| 'opened_deposit_modal'
	| 'submitted_deposit'
	| 'submitted_add_if_stake'
	| 'canceled_unstake_request'
	| 'withdrew_unstaked_assets'
	| 'opening_new_position'
	| 'submitted_unstake_request'
	| 'submitted_transfer'
	| 'submitted_withdrawal'
	| 'creating_new_subaccount'
	| 'magic_auth_login'
	| 'crossed_orderbook'
	| 'closing_position_from_popup'
	| '$pageview'
	| 'wallet_connected'
	| 'unmatched_error'
	| 'market_order_errors'
	| 'clicked_drift_earn_if_stake'
	| 'clicked_drift_earn_vaults'
	| 'clicked_drift_earn_dsol_staking'
	| 'clicked_drift_earn_amplify'
	| 'amplify_deposit_cta_click'
	| 'amplify_deposit_success'
	| 'swift_error'
	| 'account_deletion'
	| 'survey shown'
	| 'survey dismissed'
	| 'survey sent'
	| 'favorite_market_added'
	| 'favorite_market_removed'
	| 'wallet_connection_error'
	| 'referral_creation_error'
	| 'order_params_request'
	| 'order_fill_time_record'
	| 'privy_embedded_wallet_sign_and_send_error'
	| 'privy_embedded_wallet_build_and_sign__map_error'
	| 'enable_signless_trading_for_delegated_modal_confirm'
	| 'perp_non_market_order_timing'
	| 'opened_enable_signless_trading_modal'
	| 'signless_trading_setup_success'
	| 'pnl_modal_action'
	| 'export_history_modal_submit'
	| 'top_maker_feedback_notification_seen'
	| 'funxyz_copy_deposit_address'
	| 'deposit_strat_vault'
	| 'withdraw_strat_vault'
	| 'view_strat_vault'
	| 'view_overview_strat_vault'
	| 'on_page_eco_vault'
	| 'open_strat_vault_side_panel'
	| 'open_vault_detail_from_side_panel'
	| 'click_inspect_vault_from_side_panel'
	| 'top_maker_feedback_notification_seen'
	| 'transaction_timeout'
	| 'transaction_confirmation'
	| 'transaction_error'
	| 'cu_budget_exceeded'
	| 'pre_prepped_transaction'
	| 'performance_snapshot'
	| 'startup_time'
	| 'metrics_transaction_timeout'
	| 'metrics_transaction_confirmation'
	| 'metrics_transaction_error'
	| 'metrics_cu_budget_exceeded'
	| 'metrics_pre_prepped_transaction'
	| 'metrics_performance_snapshot'
	| 'metrics_startup_time';

export type PosthogConfig = {
	/**
	 * Based on the sampling rate and the overall enabled flag.
	 */
	enabled: boolean;
	consoleRecordingEnabled: boolean;
	sessionRecordingEnabled: boolean;
	capturePageViewEnabled: boolean;
	overrideEnableAllEvents: boolean;
	/**
	 * Events that are enabled based on the hardcoded config `ENABLED_EVENTS_OVERRIDE` below. This is sampling independent.
	 */
	samplingIndependentEnabledEvents: Set<PosthogEvent>;
	/**
	 * Page paths that should be captured as pageviews
	 */
	pageViewWhitelist: string[];
	wallet: {
		/**
		 * Wallet adapter names that should have all whitelisted events captured.
		 */
		walletAdapterNames: Set<string>;
		/**
		 * Events that should be captured, on top of the sampling independent events.
		 */
		eventsToTrack: Set<PosthogEvent>;
	};
};

const sampleRatio = parseFloat(
	process.env.NEXT_PUBLIC_POSTHOG_ENABLED_SAMPLE_RATIO_PCT ?? '0'
);

const samplingCheck = isNaN(sampleRatio)
	? true // if NaN, we default to true because the "overall" enabled flag is in control
	: Math.random() * 100 < sampleRatio;

const posthogEnabled = process.env.NEXT_PUBLIC_POSTHOG_ENABLED === 'true';

const SAMPLING_INDEPENDENT_ENABLED_EVENTS: PosthogEvent[] = [
	'crossed_orderbook',
	'unmatched_error',
	'market_order_errors',
	'opened_deposit_modal',
	'submitted_deposit',
	'clicked_drift_earn_if_stake',
	'clicked_drift_earn_vaults',
	'clicked_drift_earn_dsol_staking',
	'amplify_deposit_cta_click',
	'amplify_deposit_success',
	'opening_new_position',
	'swift_error',
	'submitted_withdrawal',
	'account_deletion',
	'survey shown',
	'survey dismissed',
	'survey sent',
	'favorite_market_added',
	'favorite_market_removed',
	'wallet_connection_error',
	'referral_creation_error',
	'order_params_request',
	'order_fill_time_record',
	'privy_embedded_wallet_sign_and_send_error',
	'privy_embedded_wallet_build_and_sign__map_error',
	'perp_non_market_order_timing',
	'opened_enable_signless_trading_modal',
	'signless_trading_setup_success',
	'pnl_modal_action',
	'export_history_modal_submit',
	'top_maker_feedback_notification_seen',
	'funxyz_copy_deposit_address',
	'deposit_strat_vault',
	'withdraw_strat_vault',
	'view_strat_vault',
	'view_overview_strat_vault',
	'on_page_eco_vault',
	'open_strat_vault_side_panel',
	'open_vault_detail_from_side_panel',
	'click_inspect_vault_from_side_panel',
	'top_maker_feedback_notification_seen',
	'transaction_timeout',
	'transaction_confirmation',
	'transaction_error',
	'cu_budget_exceeded',
	'pre_prepped_transaction',
	'performance_snapshot',
	'startup_time',
];

const PAGE_VIEW_WHITELIST: string[] = [
	(BASE_NAVIGATION_OPTIONS.driftEarn.handler() as RouteNavigationResult).route,
	(BASE_NAVIGATION_OPTIONS.bet.handler() as RouteNavigationResult).route,
	(BASE_NAVIGATION_OPTIONS.stats.handler() as RouteNavigationResult).route,
	(BASE_NAVIGATION_OPTIONS.vaults.handler() as RouteNavigationResult).route,
	'/SOL-PERP',
	'/BTC-PERP',
	'/ETH-PERP',
];

const WALLET_ADAPTER_NAMES_TO_ALLOW_TRACKING: string[] = [CoinbaseWalletName];
const WALLET_EVENTS_TO_TRACK: PosthogEvent[] = ['opening_new_position'];

export const POSTHOG_CONFIG: PosthogConfig = {
	enabled: samplingCheck && posthogEnabled,
	consoleRecordingEnabled:
		process.env.NEXT_PUBLIC_POSTHOG_CONSOLE_RECORDING_ENABLED === 'true',
	sessionRecordingEnabled:
		process.env.NEXT_PUBLIC_POSTHOG_SESSION_RECORDING_ENABLED === 'true',
	capturePageViewEnabled:
		process.env.NEXT_PUBLIC_POSTHOG_CAPTURE_PAGE_VIEW_ENABLED === 'true',
	overrideEnableAllEvents:
		process.env.NEXT_PUBLIC_POSTHOG_OVERRIDE_ENABLE_ALL_EVENTS === 'true',
	samplingIndependentEnabledEvents: new Set<PosthogEvent>(
		SAMPLING_INDEPENDENT_ENABLED_EVENTS
	),
	pageViewWhitelist: PAGE_VIEW_WHITELIST,
	wallet: {
		walletAdapterNames: new Set(WALLET_ADAPTER_NAMES_TO_ALLOW_TRACKING),
		eventsToTrack: new Set(WALLET_EVENTS_TO_TRACK),
	},
};
